import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../lib/spformsApi';
import { useTenant } from '../contexts/TenantContext';
import { formatDateEU } from '../utils/dateFormat';

const {
  FiRefreshCw,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiX,
  FiSave,
  FiCreditCard,
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiUsers,
  FiActivity,
} = FiIcons;

const EXPENSE_CATEGORIES = [
  { value: 'trainer', label: 'Trainer' },
  { value: 'advertising', label: 'Advertising' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'facility', label: 'Facility' },
  { value: 'travel', label: 'Travel' },
  { value: 'medical', label: 'Medical' },
  { value: 'other', label: 'Other' },
];

const INCOME_CATEGORIES = [
  { value: 'sponsor', label: 'Sponsor' },
  { value: 'donation', label: 'Donation' },
  { value: 'grant', label: 'Grant' },
  { value: 'merchandise', label: 'Merchandise' },
  { value: 'food_drinks', label: 'Food / Drinks' },
  { value: 'extra_fee', label: 'Extra Fee' },
  { value: 'other', label: 'Other' },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function emptyExpense() {
  return {
    id: null,
    title: '',
    category: 'other',
    amount: '',
    expense_date: todayIso(),
    notes: '',
  };
}

function emptyIncome() {
  return {
    id: null,
    title: '',
    category: 'other',
    amount: '',
    income_date: todayIso(),
    notes: '',
  };
}

function money(value) {
  const n = Number(value || 0);
  return `€${n.toFixed(2)}`;
}

function StatCard({ title, value, icon, tone = 'gray', subtitle }) {
  const tones = {
    gray: 'bg-gray-50 text-gray-700',
    green: 'bg-emerald-50 text-emerald-700',
    red: 'bg-red-50 text-red-700',
    blue: 'bg-indigo-50 text-indigo-700',
    amber: 'bg-amber-50 text-amber-700',
  };

  return (
    <div className="rounded-[2rem] border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
            {title}
          </div>
          <div className="mt-3 text-3xl font-black tracking-tight text-gray-900">
            {value}
          </div>
          {subtitle ? (
            <div className="mt-2 text-xs font-bold text-gray-400">{subtitle}</div>
          ) : null}
        </div>

        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${tones[tone]}`}>
          <SafeIcon icon={icon} size={22} />
        </div>
      </div>
    </div>
  );
}

export default function Finances() {
  const { tenantId } = useTenant();

  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');

  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [incomeRecords, setIncomeRecords] = useState([]);

  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState(emptyExpense());

  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [incomeForm, setIncomeForm] = useState(emptyIncome());

  const selectedGroup = useMemo(() => {
    return groups.find((group) => group.id === selectedGroupId) || null;
  }, [groups, selectedGroupId]);

  const loadGroups = async () => {
    if (!tenantId) {
      setGroups([]);
      setSelectedGroupId('');
      setLoadingGroups(false);
      return;
    }

    try {
      setLoadingGroups(true);
      const data = await spformsApi.intakeGroupsList(tenantId);
      setGroups(data || []);

      if (!selectedGroupId && data?.length > 0) {
        setSelectedGroupId(data[0].id);
      }
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadFinanceData = async () => {
    if (!tenantId || !selectedGroupId) return;

    try {
      setLoading(true);

      const [summaryData, expenseRows, incomeRows] = await Promise.all([
        spformsApi.eventFinanceSummary(tenantId, selectedGroupId),
        spformsApi.eventExpensesList(tenantId, selectedGroupId),
        spformsApi.eventIncomeList(tenantId, selectedGroupId),
      ]);

      setSummary(summaryData);
      setExpenses(expenseRows || []);
      setIncomeRecords(incomeRows || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, [tenantId]);

  useEffect(() => {
    loadFinanceData();
  }, [tenantId, selectedGroupId]);

  const openCreateExpense = () => {
    setEditingExpense(null);
    setExpenseForm(emptyExpense());
    setShowExpenseModal(true);
  };

  const openEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      id: expense.id,
      title: expense.title || '',
      category: expense.category || 'other',
      amount: expense.amount === null || expense.amount === undefined ? '' : String(expense.amount),
      expense_date: expense.expense_date || todayIso(),
      notes: expense.notes || '',
    });
    setShowExpenseModal(true);
  };

  const closeExpenseModal = () => {
    setShowExpenseModal(false);
    setEditingExpense(null);
    setExpenseForm(emptyExpense());
  };

  const saveExpense = async (event) => {
    event.preventDefault();

    if (!tenantId || !selectedGroupId) return;

    try {
      setSaving(true);

      const payload = {
        ...expenseForm,
        tenant_id: tenantId,
        intake_group_id: selectedGroupId,
      };

      if (editingExpense?.id) {
        await spformsApi.eventExpenseUpdate(payload);
      } else {
        await spformsApi.eventExpenseCreate(payload);
      }

      closeExpenseModal();
      await loadFinanceData();
    } catch (error) {
      alert(error.message || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const deleteExpense = async (expense) => {
    const confirmed = window.confirm(`Delete expense "${expense.title}"?`);
    if (!confirmed) return;

    try {
      await spformsApi.eventExpenseDelete(tenantId, expense.id);
      await loadFinanceData();
    } catch (error) {
      alert(error.message || 'Failed to delete expense');
    }
  };

  const openCreateIncome = () => {
    setEditingIncome(null);
    setIncomeForm(emptyIncome());
    setShowIncomeModal(true);
  };

  const openEditIncome = (income) => {
    setEditingIncome(income);
    setIncomeForm({
      id: income.id,
      title: income.title || '',
      category: income.category || 'other',
      amount: income.amount === null || income.amount === undefined ? '' : String(income.amount),
      income_date: income.income_date || todayIso(),
      notes: income.notes || '',
    });
    setShowIncomeModal(true);
  };

  const closeIncomeModal = () => {
    setShowIncomeModal(false);
    setEditingIncome(null);
    setIncomeForm(emptyIncome());
  };

  const saveIncome = async (event) => {
    event.preventDefault();

    if (!tenantId || !selectedGroupId) return;

    try {
      setSaving(true);

      const payload = {
        ...incomeForm,
        tenant_id: tenantId,
        intake_group_id: selectedGroupId,
      };

      if (editingIncome?.id) {
        await spformsApi.eventIncomeUpdate(payload);
      } else {
        await spformsApi.eventIncomeCreate(payload);
      }

      closeIncomeModal();
      await loadFinanceData();
    } catch (error) {
      alert(error.message || 'Failed to save income');
    } finally {
      setSaving(false);
    }
  };

  const deleteIncome = async (income) => {
    const confirmed = window.confirm(`Delete income "${income.title}"?`);
    if (!confirmed) return;

    try {
      await spformsApi.eventIncomeDelete(tenantId, income.id);
      await loadFinanceData();
    } catch (error) {
      alert(error.message || 'Failed to delete income');
    }
  };

  const income = summary?.income || {};
  const totals = summary?.totals || {};
  const expenseTotals = summary?.expenses || {};

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tighter text-gray-900">
            Event Finances
          </h1>
          <p className="text-sm font-bold text-gray-400">
            Track registrations, manual income, expenses, and event profit.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <select
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            disabled={loadingGroups}
            className="min-w-[260px] rounded-2xl border-2 border-gray-100 bg-white px-5 py-3 text-sm font-black outline-none focus:border-indigo-500"
          >
            {groups.length === 0 ? (
              <option value="">No events</option>
            ) : (
              groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.title}
                </option>
              ))
            )}
          </select>

          <button
            onClick={loadFinanceData}
            disabled={!selectedGroupId || loading}
            className="flex items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-5 py-3 text-xs font-black uppercase tracking-widest text-gray-600 transition-all hover:bg-gray-50 disabled:opacity-50"
          >
            <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>

          <button
            onClick={openCreateIncome}
            disabled={!selectedGroupId}
            className="flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-6 py-3 text-xs font-black uppercase tracking-widest text-white shadow-xl shadow-emerald-100 transition-all hover:bg-emerald-700 disabled:opacity-50"
          >
            <SafeIcon icon={FiPlus} />
            Add Income
          </button>

          <button
            onClick={openCreateExpense}
            disabled={!selectedGroupId}
            className="flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-6 py-3 text-xs font-black uppercase tracking-widest text-white shadow-xl shadow-indigo-100 transition-all hover:bg-indigo-700 disabled:opacity-50"
          >
            <SafeIcon icon={FiPlus} />
            Add Expense
          </button>
        </div>
      </div>

      {selectedGroup ? (
        <div className="rounded-[2rem] border border-indigo-100 bg-indigo-50/50 p-6">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500">
            Selected Event
          </div>
          <div className="mt-2 text-xl font-black text-indigo-950">{selectedGroup.title}</div>
          <div className="mt-1 text-sm font-bold text-indigo-500">
            {selectedGroup.location || 'No location'} · Price {money(selectedGroup.price_amount)}
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-5">
        <StatCard
          title="Registration Income"
          value={money(totals.registration_income)}
          icon={FiUsers}
          tone="green"
          subtitle={`${income.paid_count || 0} paid / ${income.partial_count || 0} partial`}
        />
        <StatCard
          title="Manual Income"
          value={money(totals.manual_income)}
          icon={FiDollarSign}
          tone="green"
          subtitle={`${income.manual_income_count || 0} income records`}
        />
        <StatCard
          title="Total Income"
          value={money(totals.total_income)}
          icon={FiTrendingUp}
          tone="green"
          subtitle="Registrations + manual income"
        />
        <StatCard
          title="Expenses"
          value={money(totals.total_expenses)}
          icon={FiTrendingDown}
          tone="red"
          subtitle={`${expenseTotals.expenses_count || 0} expense records`}
        />
        <StatCard
          title="Net Profit"
          value={money(totals.net_profit)}
          icon={Number(totals.net_profit || 0) >= 0 ? FiTrendingUp : FiTrendingDown}
          tone={Number(totals.net_profit || 0) >= 0 ? 'green' : 'red'}
          subtitle="Total income minus expenses"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <StatCard title="Cash" value={money(income.cash_amount)} icon={FiCreditCard} tone="gray" />
        <StatCard title="Card" value={money(income.card_amount)} icon={FiCreditCard} tone="blue" />
        <StatCard title="Wire" value={money(income.wire_amount)} icon={FiCreditCard} tone="gray" />
        <StatCard title="Outstanding" value={money(totals.outstanding_amount)} icon={FiActivity} tone="amber" />
        <StatCard title="Approved Players" value={income.approved_count || 0} icon={FiUsers} tone="blue" />
      </div>

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-3">
        <div className="xl:col-span-2 rounded-[2.5rem] border border-gray-100 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b bg-gray-50/50 px-8 py-6">
            <div>
              <h2 className="text-xl font-black text-gray-900">Manual Income</h2>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Sponsors, donations, grants, sales, and extra income
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b bg-white text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                <tr>
                  <th className="px-8 py-5">Income</th>
                  <th className="px-8 py-5">Category</th>
                  <th className="px-8 py-5">Date</th>
                  <th className="px-8 py-5 text-right">Amount</th>
                  <th className="px-8 py-5 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="p-20 text-center text-xs font-black uppercase tracking-[0.3em] text-gray-400 animate-pulse">
                      Loading income...
                    </td>
                  </tr>
                ) : incomeRecords.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="p-20 text-center text-sm font-bold text-gray-400">
                      No manual income yet. Add sponsors, donations, grants, or extra sales.
                    </td>
                  </tr>
                ) : (
                  incomeRecords.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50/50">
                      <td className="px-8 py-5">
                        <div className="font-black text-gray-900">{record.title}</div>
                        {record.notes ? (
                          <div className="mt-1 text-xs font-bold text-gray-400">{record.notes}</div>
                        ) : null}
                      </td>
                      <td className="px-8 py-5">
                        <span className="rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700">
                          {record.category}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-sm font-bold text-gray-500">
                        {formatDateEU(record.income_date)}
                      </td>
                      <td className="px-8 py-5 text-right font-black text-emerald-600">
                        {money(record.amount)}
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEditIncome(record)}
                            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-indigo-50 hover:text-indigo-600"
                          >
                            <SafeIcon icon={FiEdit2} />
                          </button>
                          <button
                            onClick={() => deleteIncome(record)}
                            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-600"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-[2.5rem] border border-gray-100 bg-white p-8 shadow-sm">
          <h2 className="text-xl font-black text-gray-900">Income Breakdown</h2>
          <p className="mt-1 text-xs font-bold uppercase tracking-widest text-gray-400">
            Manual income by category
          </p>

          <div className="mt-8 space-y-4">
            {INCOME_CATEGORIES.map((category) => {
              const key = category.value === 'other' ? 'other_income_amount' : `${category.value}_amount`;
              const value = income[key] || 0;

              return (
                <div key={category.value} className="flex items-center justify-between rounded-2xl bg-gray-50 px-5 py-4">
                  <div className="text-xs font-black uppercase tracking-widest text-gray-500">
                    {category.label}
                  </div>
                  <div className="text-sm font-black text-gray-900">{money(value)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-3">
        <div className="xl:col-span-2 rounded-[2.5rem] border border-gray-100 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b bg-gray-50/50 px-8 py-6">
            <div>
              <h2 className="text-xl font-black text-gray-900">Expenses</h2>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Simple manual event costs
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b bg-white text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                <tr>
                  <th className="px-8 py-5">Expense</th>
                  <th className="px-8 py-5">Category</th>
                  <th className="px-8 py-5">Date</th>
                  <th className="px-8 py-5 text-right">Amount</th>
                  <th className="px-8 py-5 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="p-20 text-center text-xs font-black uppercase tracking-[0.3em] text-gray-400 animate-pulse">
                      Loading expenses...
                    </td>
                  </tr>
                ) : expenses.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="p-20 text-center text-sm font-bold text-gray-400">
                      No expenses yet. Add trainer, advertising, facility, or other costs.
                    </td>
                  </tr>
                ) : (
                  expenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-gray-50/50">
                      <td className="px-8 py-5">
                        <div className="font-black text-gray-900">{expense.title}</div>
                        {expense.notes ? (
                          <div className="mt-1 text-xs font-bold text-gray-400">{expense.notes}</div>
                        ) : null}
                      </td>
                      <td className="px-8 py-5">
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-gray-600">
                          {expense.category}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-sm font-bold text-gray-500">
                        {formatDateEU(expense.expense_date)}
                      </td>
                      <td className="px-8 py-5 text-right font-black text-red-600">
                        {money(expense.amount)}
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEditExpense(expense)}
                            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-indigo-50 hover:text-indigo-600"
                          >
                            <SafeIcon icon={FiEdit2} />
                          </button>
                          <button
                            onClick={() => deleteExpense(expense)}
                            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-600"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-[2.5rem] border border-gray-100 bg-white p-8 shadow-sm">
          <h2 className="text-xl font-black text-gray-900">Expense Breakdown</h2>
          <p className="mt-1 text-xs font-bold uppercase tracking-widest text-gray-400">
            By category
          </p>

          <div className="mt-8 space-y-4">
            {EXPENSE_CATEGORIES.map((category) => {
              const key = `${category.value}_amount`;
              const value = expenseTotals[key] || 0;

              return (
                <div key={category.value} className="flex items-center justify-between rounded-2xl bg-gray-50 px-5 py-4">
                  <div className="text-xs font-black uppercase tracking-widest text-gray-500">
                    {category.label}
                  </div>
                  <div className="text-sm font-black text-gray-900">{money(value)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showIncomeModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
          <div className="w-full max-w-xl overflow-hidden rounded-[2.5rem] bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b bg-gray-50/50 p-8">
              <div>
                <h2 className="text-2xl font-black text-gray-900">
                  {editingIncome ? 'Edit Income' : 'Add Income'}
                </h2>
                <p className="mt-1 text-xs font-bold uppercase tracking-widest text-gray-400">
                  Manual event income record
                </p>
              </div>

              <button
                type="button"
                onClick={closeIncomeModal}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-600"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            <form onSubmit={saveIncome} className="space-y-6 p-8">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                  Title
                </label>
                <input
                  required
                  value={incomeForm.title}
                  onChange={(e) => setIncomeForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Main sponsor"
                  className="w-full rounded-2xl border-2 border-gray-100 px-5 py-4 text-sm font-bold outline-none focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                    Category
                  </label>
                  <select
                    value={incomeForm.category}
                    onChange={(e) => setIncomeForm((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full rounded-2xl border-2 border-gray-100 px-5 py-4 text-sm font-bold outline-none focus:border-indigo-500"
                  >
                    {INCOME_CATEGORIES.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                    Amount (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={incomeForm.amount}
                    onChange={(e) => setIncomeForm((prev) => ({ ...prev, amount: e.target.value }))}
                    className="w-full rounded-2xl border-2 border-gray-100 px-5 py-4 text-sm font-black outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                  Date
                </label>
                <input
                  type="date"
                  value={incomeForm.income_date}
                  onChange={(e) => setIncomeForm((prev) => ({ ...prev, income_date: e.target.value }))}
                  className="w-full rounded-2xl border-2 border-gray-100 px-5 py-4 text-sm font-bold outline-none focus:border-indigo-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                  Notes
                </label>
                <textarea
                  rows={3}
                  value={incomeForm.notes}
                  onChange={(e) => setIncomeForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes"
                  className="w-full rounded-2xl border-2 border-gray-100 px-5 py-4 text-sm font-bold outline-none focus:border-indigo-500"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="flex w-full items-center justify-center gap-3 rounded-2xl bg-emerald-600 py-5 text-xs font-black uppercase tracking-[0.2em] text-white shadow-xl shadow-emerald-100 hover:bg-emerald-700 disabled:opacity-60"
              >
                <SafeIcon icon={FiSave} />
                {saving ? 'Saving...' : 'Save Income'}
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {showExpenseModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
          <div className="w-full max-w-xl overflow-hidden rounded-[2.5rem] bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b bg-gray-50/50 p-8">
              <div>
                <h2 className="text-2xl font-black text-gray-900">
                  {editingExpense ? 'Edit Expense' : 'Add Expense'}
                </h2>
                <p className="mt-1 text-xs font-bold uppercase tracking-widest text-gray-400">
                  Event cost record
                </p>
              </div>

              <button
                type="button"
                onClick={closeExpenseModal}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-600"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            <form onSubmit={saveExpense} className="space-y-6 p-8">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                  Title
                </label>
                <input
                  required
                  value={expenseForm.title}
                  onChange={(e) => setExpenseForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Trainer payment"
                  className="w-full rounded-2xl border-2 border-gray-100 px-5 py-4 text-sm font-bold outline-none focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                    Category
                  </label>
                  <select
                    value={expenseForm.category}
                    onChange={(e) => setExpenseForm((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full rounded-2xl border-2 border-gray-100 px-5 py-4 text-sm font-bold outline-none focus:border-indigo-500"
                  >
                    {EXPENSE_CATEGORIES.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                    Amount (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm((prev) => ({ ...prev, amount: e.target.value }))}
                    className="w-full rounded-2xl border-2 border-gray-100 px-5 py-4 text-sm font-black outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                  Date
                </label>
                <input
                  type="date"
                  value={expenseForm.expense_date}
                  onChange={(e) => setExpenseForm((prev) => ({ ...prev, expense_date: e.target.value }))}
                  className="w-full rounded-2xl border-2 border-gray-100 px-5 py-4 text-sm font-bold outline-none focus:border-indigo-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                  Notes
                </label>
                <textarea
                  rows={3}
                  value={expenseForm.notes}
                  onChange={(e) => setExpenseForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes"
                  className="w-full rounded-2xl border-2 border-gray-100 px-5 py-4 text-sm font-bold outline-none focus:border-indigo-500"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="flex w-full items-center justify-center gap-3 rounded-2xl bg-indigo-600 py-5 text-xs font-black uppercase tracking-[0.2em] text-white shadow-xl shadow-indigo-100 hover:bg-indigo-700 disabled:opacity-60"
              >
                <SafeIcon icon={FiSave} />
                {saving ? 'Saving...' : 'Save Expense'}
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}